import { createActions, handleActions } from 'redux-actions';
import { setAccessToken } from '@keypr/redux-json-api';
import _ from 'lodash';
import {
  getMe,
  getAffiliate,
  getAffiliates,
  getPermissions,
  setAvailable,
} from '../api';
import {
  getUserChannels,
  getAffiliateChannels,
} from '../api/ticket';
import { loadSession, updateSession } from './session';
import { updateStateReducer } from '../utils/redux';
import { sendCommand, isDevice } from '../utils/mobile';
import { setDeviceLanguage } from '../state/actions/locale';
import GtmService from '../services/GtmService';

const actions = createActions({},
  'APP_SESSION_LOAD',
  'APP_UPDATE_STATE',
  'APP_ACTIVE_USER_LOADED',
  'APP_SELECT_AFFILIATE',
  'APP_RESOURCES_LOADED',
  'APP_TOGGLE_AVAILABLE',
);

export const appStates = {
  INITIAL: 'initial',
  AUTH: 'auth',
  SELECT_AFFILIATE: 'selectAffiliate',
  READY: 'ready',
};

const defaultState = {
  state: appStates.INITIAL,
  isLoading: false,
  singleAffiliateUser: true,

  accessToken: null,
  userId: null,
  affiliateId: null,
  isSuperUser: false,

  user: null,
  permissions: null,
  channels: null,
  affiliate: null,
};

export const reducers = handleActions({
  [actions.appSessionLoad]: updateStateReducer,
  [actions.appUpdateState]: updateStateReducer,
  [actions.appActiveUserLoaded]: updateStateReducer,
  [actions.appResourcesLoaded]: updateStateReducer,
  [actions.appSelectAffiliate]: updateStateReducer,
  [actions.appSelectAffiliate]: updateStateReducer,
  [actions.appToggleAvailable]: updateStateReducer,
}, defaultState);

const loadApplicationResources = getCurrentLanguage =>
  (dispatch, getState) => {
    const {
      affiliateId,
      userId,
      isSuperUser,
      user: userCached, // Can be already loaded on auth
      affiliate: affiliateCache, // Can be already loaded on select affiliate screen
      accessToken,
    } = getState().session;

    dispatch(actions.appUpdateState({ isLoading: true }));

    const getChannels = () =>
      (isSuperUser ? getAffiliateChannels(affiliateId) : getUserChannels(userId, affiliateId));

    const getAffiliateRequests = () => [
      (affiliateCache || getAffiliate(affiliateId)),
      getPermissions(userId, affiliateId),
      getChannels(),
    ];

    return Promise.all([
      userCached || getMe(),
      ...(affiliateId ? getAffiliateRequests() : []),
    ]).then(([user, affiliate = null, permissions = null, channels = null]) => {
      if (affiliate) {
        sendCommand('login', {
          user: { ...user.attributes, id: user.id },
          affiliate: { id: affiliateId },
          auth: { access_token: accessToken },
          permissions,
        });
        sendCommand('app-ready');
      }

      dispatch(actions.appResourcesLoaded({
        state: affiliate ? appStates.READY : appStates.SELECT_AFFILIATE,
        user,
        isSuperUser: user.attributes.is_superuser,
        affiliate,
        channels,
        permissions,
        isLoading: false,
      }));

      // Set up default data layer affiliate and user
      GtmService.setupDefaultDataLayer({
        affiliate: affiliate ? affiliate.attributes.name : null,
        isStaffUser: user && (
          user.attributes.is_staff
          || user.attributes.is_superuser
        ),
      });

      const isLocalizationEnabled = affiliate && affiliate.attributes.config && affiliate.attributes.config.is_localization_enabled;

      // Setting the Initial Device Language for native component to localize when app is launched
      if (isLocalizationEnabled
        && isDevice()
        && getCurrentLanguage) {
        const lang = getCurrentLanguage();
        setDeviceLanguage(lang);
      }
    });
  };

export const toggleAvailability = isAvailable =>
  (dispatch, getState) => {
    const {
      session: {
        permissions,
        affiliateId,
      },
    } = getState();

    const userPermission = permissions.user;
    const available = isAvailable || !_.get(userPermission, 'attributes.is_available', false);

    return setAvailable(affiliateId, userPermission.id, available)
      .then((permission) => {
        dispatch(actions.appToggleAvailable({
          permissions: {
            ...permissions,
            user: permission,
          },
        }));
      });
  };

export const selectAffiliate = affiliate =>
  (dispatch) => {
    dispatch(actions.appSelectAffiliate({
      affiliateId: affiliate.id,
      affiliate,
    }));

    updateSession({
      affiliateId: affiliate.id,
    });

    return dispatch(loadApplicationResources());
  };

export const createAppSession = () =>
  (dispatch) => {
    dispatch(actions.appUpdateState({ isLoading: true }));

    return Promise.all([
      getMe(),
      getAffiliates(2),
    ]).then(([user, affiliates]) => {
      const singleAffiliateUser = affiliates.length === 1;

      const affiliate = affiliates[0];
      const affiliateId = affiliate && affiliate.id;

      const isSuperUser = !!user.attributes.is_superuser;

      dispatch(actions.appActiveUserLoaded({
        isLoading: false,
        singleAffiliateUser,
        user,
        userId: user.id,
        isSuperUser,
        ...singleAffiliateUser && { affiliate, affiliateId },
      }));

      updateSession({
        userId: user.id,
        singleAffiliateUser,
        isSuperUser,
        ...singleAffiliateUser && { affiliateId },
      });
    });
  };

export const loadAppSession = getCurrentLanguage =>
  (dispatch) => {
    const session = loadSession();

    if (!(session && session.accessToken)) {
      // Switch State
      return dispatch(actions.appSessionLoad({
        state: appStates.AUTH,
      }));
    }

    dispatch(setAccessToken(session.accessToken));
    dispatch(actions.appSessionLoad(session));

    const createPromise = session.userId ?
      Promise.resolve() :
      dispatch(createAppSession());
    return createPromise
      .then(() => dispatch(loadApplicationResources(getCurrentLanguage)));
  };



// WEBPACK FOOTER //
// ./src/app/state.js